import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isBetween from "dayjs/plugin/isBetween";
import { DATE_INPUT_TEXT_FORMAT } from "src/constants/format";
import { logWarn } from "./logger";

export const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";
export const DATE_LONG_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";
export const DATE_PICKER_DATE_FORMAT = "dd/MM/yyyy";
export const MOTOR_SUMMARY_DATE_FORMAT = "DD MMM YYYY";
export const ORDER_LIST_DATE_SHORT_FORMAT = "DD MMM YYYY";
export const ORDER_LIST_DATE_HOUR_MINUTE_FORMAT = "DD MMM YYYY HH:mm";
export const ORDER_LIST_DATE_LONG_FORMAT = "DD MMM YYYY HH:mm:ss";

export const getNow = () => dayjs();
export const getCurrentDate = () => getNow().startOf("day");

export const parseDate = (date, format = DEFAULT_DATE_FORMAT) => {
  if (dayjs.isDayjs(date)) {
    return date;
  }

  const newDate = dayjs(date, format);
  if (newDate.isValid()) {
    return newDate;
  }

  throw new TypeError("Given date is invalid.");
};

export const formatDateText = (
  date: Date | Dayjs,
  dateFormat: string = DATE_INPUT_TEXT_FORMAT
) => {
  const dayjsDate = dayjs(date, DATE_INPUT_TEXT_FORMAT);
  if (dayjsDate.isValid()) {
    return dayjsDate.format(dateFormat);
  }

  logWarn("Given date is invalid.");
  return "";
};

export const formatThaiBuddhistDate = (date: Date | Dayjs | string) => {
  const dayjsDate = dayjs(date);

  if (!dayjsDate.isValid()) {
    return "";
  }

  return new Intl.DateTimeFormat("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(dayjsDate.toDate());
};

export const formatLocalizedDateText = (
  date: Date | Dayjs,
  dateFormat: string,
  language: string
) => {
  const dayjsDate = dayjs(date, dateFormat).locale(language);
  if (dayjsDate.isValid()) {
    return dayjsDate.format(dateFormat);
  }

  logWarn("Given date is invalid.");
  return "";
};

export const isBeforeDate = (input, date = getCurrentDate()) => {
  try {
    return date.isAfter(parseDate(input).endOf("day"));
  } catch {
    return false;
  }
};

export const isAfterDate = (input, date = getCurrentDate()) => {
  try {
    return date.isBefore(parseDate(input).startOf("day"));
  } catch {
    return false;
  }
};
export const isAfterDateTime = (date: string | Date | Dayjs) => {
  try {
    return dayjs().isAfter(dayjs(date));
  } catch (error) {
    return false;
  }
};

export const isBetweenDate = ({
  from,
  to,
  date = getCurrentDate(),
  edgeCase = false,
  format = DEFAULT_DATE_FORMAT,
}) => {
  try {
    dayjs.extend(isSameOrAfter);
    dayjs.extend(isSameOrBefore);
    dayjs.extend(isBetween);
    const parsedDate = parseDate(date);
    if (edgeCase) {
      return (
        parsedDate.isSameOrAfter(dayjs(from, format).startOf("day")) &&
        parsedDate.isSameOrBefore(dayjs(to, format).endOf("day"))
      );
    }
    return parsedDate.isBetween(from, to);
  } catch {
    return false;
  }
};

export const isToday = (currentDate) =>
  currentDate && getNow().isSame(currentDate, "day");

export const isInDatePeriod = ({
  from,
  to,
  date = new Date(),
  edgeCase = false,
}) => {
  try {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    if (edgeCase) {
      return (!from || date >= fromDate) && (!to || date <= toDate);
    }

    return (!from || date > fromDate) && (!to || date < toDate);
  } catch {
    return false;
  }
};
